import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { CalendarXIcon, CalendarIcon, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useQueryState } from "nuqs";
import { PlanOfCareDetails } from "../../hooks/usePlanOfCareDetails";
import { Card, CardContent, CardFooter } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

type FlatAppt = NonNullable<PlanOfCareDetails["planOfCares"][0]["appointments"]>[0];

type EnrichedAppt = FlatAppt & {
  caseStudyName: string;
  durationMinutes: number;
};

function isSameDate(apptStart: string, cancelDate: string): boolean {
  return apptStart.substring(0, 10) === cancelDate.substring(0, 10);
}

export function CancelView({ uniqueId, detailsData, cancelVisit, onCancelled }: { uniqueId?: string; detailsData: PlanOfCareDetails; cancelVisit: string; onCancelled: () => Promise<unknown> }) {
  const [, setCancelVisit] = useQueryState("cancel_visit");
  const { agent, planOfCares, organization } = detailsData;
  const cancelEnabled = Boolean(agent.cancelEnabled);
  const chargeCancelFee = Boolean(agent.chargeCancellationFee);
  const cancellationFeeWindow = agent.cancellationFee?.window;
  const cancellationFeeAmount = agent.cancellationFee?.amount;
  const noCancellationWindowHours = agent.noCancellationWindowHours;
  const hasMultiplePOCs = planOfCares.length > 1;

  const now = DateTime.now();

  const cancellableAppointments = useMemo<EnrichedAppt[]>(() => {
    const result: EnrichedAppt[] = [];

    for (const poc of planOfCares) {
      for (const appt of poc.appointments ?? []) {
        if (appt.apptStatus === "Canc" || appt.apptStatus === "NoSh") continue;
        if (!appt.start) continue;

        const hoursUntil = DateTime.fromISO(appt.start).diff(now, "hours").hours;
        if (hoursUntil <= 0) continue;
        if (noCancellationWindowHours && hoursUntil < noCancellationWindowHours) continue;

        const durationMinutes = appt.end ? Math.round(DateTime.fromISO(appt.end).diff(DateTime.fromISO(appt.start), "minutes").minutes) : 0;

        result.push({ ...appt, caseStudyName: poc.caseStudyName, durationMinutes });
      }
    }

    return result.sort((a, b) => DateTime.fromISO(a.start).toMillis() - DateTime.fromISO(b.start).toMillis());
  }, [planOfCares, noCancellationWindowHours, now]);

  return (
    <div className="py-4 xl:py-8 border-t xl:border-l xl:border-t-0 border-border flex items-start shrink-0">
      <div className="flex flex-col gap-4 w-full">
        <div className="px-8 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Upcoming Visits</h2>
            <p className="text-sm text-muted-foreground">You can view and manage your upcoming appointments here.</p>
          </div>
          <Button size="sm" variant="outline" className="shrink-0" onClick={() => setCancelVisit(null)}>
            <CalendarIcon className="size-3.5" />
            Book a new visit
          </Button>
        </div>

        {cancellableAppointments.length === 0 ? (
          <div className="py-2 px-3">
            <div className="min-w-[290px] w-full">
              <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground py-8 xl:py-12">
                <div className="flex items-center justify-center size-10 rounded-full bg-accent">
                  <CalendarXIcon className="size-5 text-muted-foreground" aria-hidden="true" />
                </div>
                <p className="text-sm text-center">No upcoming appointments.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2 xl:h-[352px] xl:overflow-y-auto px-6">
            {(() => {
              const matchCount = cancellableAppointments.filter((a) => isSameDate(a.start, cancelVisit)).length;
              return cancellableAppointments.map((appt) => (
                <CancelAppointmentCard
                  key={appt.id}
                  appointment={appt}
                  uniqueId={uniqueId}
                  showCaseStudy={hasMultiplePOCs}
                  cancelEnabled={cancelEnabled}
                  chargeCancelFee={chargeCancelFee}
                  cancellationFeeWindow={cancellationFeeWindow}
                  cancellationFeeAmount={cancellationFeeAmount}
                  onCancelled={onCancelled}
                  autoOpen={matchCount === 1 && isSameDate(appt.start, cancelVisit)}
                />
              ));
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function CancelAppointmentCard({
  appointment,
  uniqueId,
  showCaseStudy,
  cancelEnabled,
  chargeCancelFee,
  cancellationFeeWindow,
  cancellationFeeAmount,
  onCancelled,
  autoOpen,
}: {
  appointment: EnrichedAppt;
  uniqueId?: string;
  showCaseStudy: boolean;
  cancelEnabled: boolean;
  chargeCancelFee: boolean;
  cancellationFeeWindow: "Always" | number | undefined;
  cancellationFeeAmount: number | undefined;
  onCancelled: () => Promise<unknown>;
  autoOpen: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (autoOpen) setIsOpen(true);
  }, [autoOpen]);

  const requiresFee = useMemo(() => {
    if (!chargeCancelFee) return false;
    if (cancellationFeeWindow === "Always") return true;
    if (typeof cancellationFeeWindow === "number") {
      const hours = DateTime.fromISO(appointment.start).diff(DateTime.now(), "hours").hours;
      return hours >= 0 && hours <= cancellationFeeWindow;
    }
    return false;
  }, [chargeCancelFee, cancellationFeeWindow, appointment.start]);

  const feeLabel = useMemo(() => {
    if (typeof cancellationFeeAmount !== "number") return "";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cancellationFeeAmount / 100);
  }, [cancellationFeeAmount]);

  const dateLabel = DateTime.fromISO(appointment.start).toFormat("EEEE, MMMM d 'at' h:mm a");

  const dialogDescription = `This will cancel your scheduled appointment on ${dateLabel}.${requiresFee ? ` This cancellation requires a late fee${feeLabel ? ` of ${feeLabel}` : ""}.` : ""}`;

  const handleCancel = useCallback(async () => {
    if (!uniqueId) {
      toast.error("Unable to cancel this appointment right now. Please try again.");
      return;
    }
    setIsCancelling(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/cancel`, {
        uniqueId,
        appointmentDate: appointment.start,
        reason: "Cancelled by patient from plan-of-care booking page",
        returnUrl: window.location.href,
      });
      const { requiresPayment, checkoutUrl, message } = response.data || {};
      if (requiresPayment && checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      await onCancelled();
      setIsOpen(false);
      toast.success(message || "Appointment cancelled successfully.");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "There was an error cancelling your appointment. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  }, [uniqueId, appointment.start, onCancelled]);

  return (
    <Card className="py-2 gap-1 min-w-[312px]">
      <CardContent className="px-3">
        <div className="flex items-center justify-between gap-3 w-full">
          <div>
            <p className="text-base font-medium tracking-tight">{appointment.calTitle}</p>
            {showCaseStudy && <p className="text-xs text-muted-foreground">{appointment.caseStudyName}</p>}
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>{DateTime.fromISO(appointment.start).toFormat("MMM d, yyyy")}</span>
              <span className="inline-block size-1 rounded-full bg-muted-foreground/50" />
              <span>{DateTime.fromISO(appointment.start).toFormat("h:mm a")}</span>
              {appointment.durationMinutes > 0 && (
                <>
                  <span className="inline-block size-1 rounded-full bg-muted-foreground/50" />
                  <span>{appointment.durationMinutes} min</span>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      {cancelEnabled && <CardFooter className="pt-0 px-2 justify-end">
        <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="!border-red-500/30 text-red-900 dark:text-red-200 bg-red-500/10 hover:bg-red-500/20 hover:text-red-900 dark:hover:text-red-200"
              disabled={isCancelling}
            >
              Cancel
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel appointment?</AlertDialogTitle>
              <AlertDialogDescription>{dialogDescription}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isCancelling}>Keep appointment</AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  variant="destructive"
                  disabled={isCancelling}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCancel();
                  }}
                >
                  {isCancelling && <Loader2 className="size-4 animate-spin" />}
                  Confirm cancel
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardFooter>}
    </Card>
  );
}
